const INTERNAL_PROSPECT_FIELDS = new Set(["_firebaseKey", "__commercialProjection"]);
const SERVER_OWNED_PROSPECT_TIMESTAMP_FIELDS = new Set([
  "createdAt",
  "updatedAt",
  "created_at",
  "updated_at",
  "modifiedAt",
]);

function isUnpatchableProspectField(key) {
  return key === "id"
    || INTERNAL_PROSPECT_FIELDS.has(key)
    || SERVER_OWNED_PROSPECT_TIMESTAMP_FIELDS.has(key);
}

function validServerUpdatedAt(value) {
  if (typeof value !== "string"
    || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(value)) return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function idOf(record) {
  return record?.id == null ? null : String(record.id);
}

function jsonEquivalent(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function stripInternalFields(record) {
  return Object.fromEntries(Object.entries(record || {}).filter(([key]) => !INTERNAL_PROSPECT_FIELDS.has(key)));
}

export function diffProspectFields(current, next) {
  const before = stripInternalFields(current);
  const after = stripInternalFields(next);
  const changes = {};
  Object.keys(before).forEach(key => {
    if (!isUnpatchableProspectField(key) && !Object.prototype.hasOwnProperty.call(after, key)) changes[key] = null;
  });
  Object.entries(after).forEach(([key, value]) => {
    if (isUnpatchableProspectField(key)) return;
    if (!Object.prototype.hasOwnProperty.call(before, key) || !jsonEquivalent(before[key], value)) {
      changes[key] = value === undefined ? null : value;
    }
  });
  return changes;
}

// Absence in an incoming scoped projection never means delete: only an explicit
// user delete action may generate a delete request.
export function planProspectItemOperations(currentRecords, incomingRecords, { deleteIds = [], patches = [] } = {}) {
  const current = Array.isArray(currentRecords) ? currentRecords.filter(Boolean) : [];
  const incoming = Array.isArray(incomingRecords) ? incomingRecords.filter(Boolean) : [];
  const previousById = new Map(current.map(record => [idOf(record), record]).filter(([id]) => id !== null));
  const nextById = new Map(incoming.map(record => [idOf(record), record]).filter(([id]) => id !== null));
  const deletions = new Set([...deleteIds].map(String));
  const patchById = new Map(patches
    .filter(patch => patch?.id != null && patch?.changes && typeof patch.changes === "object")
    .map(patch => [String(patch.id), patch]));
  const operations = [];

  incoming.forEach(record => {
    const id = idOf(record);
    if (id === null) throw new Error("O cartão de prospecção precisa de um ID estável antes de ser salvo.");
    if (deletions.has(id)) return;
    const previous = previousById.get(id);
    if (!previous) {
      operations.push({ operation: "create", id, record: stripInternalFields(record), storeId: record.storeId });
      return;
    }
    const explicitPatch = patchById.get(id);
    const changes = explicitPatch
      ? Object.fromEntries(Object.entries(explicitPatch.changes)
        .filter(([key]) => !isUnpatchableProspectField(key)))
      : diffProspectFields(previous, record);
    if (Object.keys(changes).length) {
      operations.push({
        operation: "patch",
        id,
        key: previous._firebaseKey || record._firebaseKey || null,
        storeId: previous.storeId ?? record.storeId,
        changes,
      });
    }
  });

  patchById.forEach((patch, id) => {
    if (deletions.has(id) || nextById.has(id)) return;
    const previous = previousById.get(id);
    if (!previous) throw new Error("O cartão de prospecção não está disponível neste escopo.");
    const changes = Object.fromEntries(Object.entries(patch.changes)
      .filter(([key]) => !isUnpatchableProspectField(key)));
    if (!Object.keys(changes).length) return;
    operations.push({
      operation: "patch",
      id,
      key: previous._firebaseKey || null,
      storeId: previous.storeId ?? patch.storeId,
      changes,
    });
  });

  deletions.forEach(id => {
    const previous = previousById.get(id);
    if (!previous) return;
    operations.push({
      operation: "delete",
      id,
      key: previous._firebaseKey || null,
      storeId: previous.storeId,
    });
  });
  return operations;
}

export function mergeConfirmedProspectItem(records, confirmed) {
  if (!confirmed || confirmed.id == null) throw new Error("A gravação não confirmou um cartão de prospecção.");
  const id = String(confirmed.id);
  const current = Array.isArray(records) ? records : [];
  const phone = String(confirmed.phone || confirmed.telefone || "").replace(/\D/g, "");
  const storeId = Number(confirmed.storeId ?? confirmed.lojaAtual) || null;
  const sameStorePhone = record => Boolean(phone && storeId
    && Number(record?.storeId ?? record?.lojaAtual) === storeId
    && String(record?.phone || record?.telefone || "").replace(/\D/g, "") === phone);
  let index = current.findIndex(record => idOf(record) === id);
  if (index < 0) index = current.findIndex(sameStorePhone);
  if (index < 0) return [...current, confirmed];
  const previous = current[index];
  const previousUpdatedAt = validServerUpdatedAt(previous?.updatedAt);
  const confirmedUpdatedAt = validServerUpdatedAt(confirmed.updatedAt);
  if (idOf(previous) === id
    && previousUpdatedAt !== null
    && confirmedUpdatedAt !== null
    && confirmedUpdatedAt < previousUpdatedAt) return current;
  const merged = current.map((record, recordIndex) => recordIndex === index ? { ...record, ...confirmed } : record);
  return merged.filter((record, recordIndex) => recordIndex === index || !sameStorePhone(record));
}

export function firstConfirmedProspectWrite(results) {
  const operations = Array.isArray(results) ? results : [results];
  return operations.find(result => result?.record) || null;
}

export function prospectWriteWasStale(results) {
  const operations = Array.isArray(results) ? results : [results];
  return operations.some(result => result?.stale === true);
}

export function runProspectSideEffectIfCurrent(results, { requestScope, currentScope, sideEffect }) {
  if (prospectWriteWasStale(results)
    || !prospectOperationIsCurrent({ requestScope, currentScope })) return false;
  sideEffect?.();
  return true;
}

export function prospectWriteScope({ operator, currentUser, demoMode, storeId, generation = 0 }) {
  return JSON.stringify([
    operator?.id ?? null,
    operator?.role ?? null,
    currentUser?.id ?? null,
    currentUser?.role ?? null,
    Number(storeId ?? currentUser?.storeId) || null,
    Boolean(demoMode),
    Number(generation) || 0,
  ]);
}

export function prospectOperationIsCurrent({ requestScope, currentScope }) {
  return requestScope === currentScope;
}

export function fenceProspectHttpResponse({ requestScope, currentScope, status, onUnauthorized }) {
  if (!prospectOperationIsCurrent({ requestScope, currentScope })) return { stale:true };
  if (Number(status) === 401) {
    onUnauthorized?.();
    return { stale:false, sessionExpired:true };
  }
  return { stale:false, sessionExpired:false };
}

export function createProspectId(randomUUID = () => {
  const secureCrypto = typeof window !== "undefined" ? window.crypto : undefined;
  if (!secureCrypto) throw new Error("Este navegador não permite gerar IDs exclusivos com segurança.");
  if (typeof secureCrypto.randomUUID === "function") return secureCrypto.randomUUID();
  if (typeof secureCrypto.getRandomValues !== "function") {
    throw new Error("Este navegador não permite gerar IDs exclusivos com segurança.");
  }
  const bytes = secureCrypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  return [...bytes].map((byte, index) => (
    [4, 6, 8, 10].includes(index) ? `-${byte.toString(16).padStart(2, "0")}` : byte.toString(16).padStart(2, "0")
  )).join("");
}) {
  const id = randomUUID();
  if (typeof id !== "string" || !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id)) {
    throw new Error("Não foi possível gerar um ID exclusivo para o cartão de prospecção.");
  }
  return `prospect_${id}`;
}
